import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Tooltip,
  TextField,
  Autocomplete,
  Chip,
  Grid,
  LinearProgress,
  Checkbox,
  IconButton,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import { useDispatch } from "react-redux";
import Card from "components/card";
import DataGridMarifer from "components/dataGridMarifer/dataGridMarifer";
import { columns } from "../columns";
import { getAllInsumoComplete } from "../functions";
import { getAllInsumos } from "components-base/insumos/functions";
import StackInsumosDrawer from "./StackInsumosDrawer";
import StackInsumosDelete from "./StackInsumosDelete";
import ModalPDFInsumos from "./ModalPDFInsumos";
import VariantLabelModal from "./VariantLabelModal";

const MOVEMENT_TYPES = [
  { label: "ENTRADA", color: "success" },
  { label: "SALIDA", color: "error" },
  { label: "AJUSTE", color: "warning" },
];

// Calcula stock desde los movimientos para el panel de inventario
const calcStock = (movimientos) => {
  const sorted = [...(movimientos || [])].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  );
  let total = 0;
  for (const m of sorted) {
    const qty = parseFloat(m.quantity) || 0;
    switch (m.movementType) {
      case "ENTRADA": total += qty; break;
      case "SALIDA":  total = Math.max(0, total - qty); break;
      case "AJUSTE":  total = qty; break;
      default: break;
    }
  }
  return parseFloat(Math.max(0, total).toFixed(2));
};

const StackInsumosTable = () => {
  const [open, setOpen] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [mode, setMode] = useState("POST");
  const [movementsData, setMovementsData] = useState(null);
  const [dataColumns, setDataColumns] = useState(null);
  const [insumos, setInsumos] = useState([]);
  const [selectedInsumo, setSelectedInsumo] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [filterInsumo, setFilterInsumo] = useState("");
  const [openPDF, setOpenPDF] = useState(false);
  const [openQR, setOpenQR] = useState(false);
  const [qrVariants, setQrVariants] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const dispatch = useDispatch();

  const toggleSelected = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const printVariants = (list) => {
    setQrVariants(list);
    setOpenQR(true);
  };

  // Variantes únicas: se toma el movimiento más reciente por variant.id para tener el stock actualizado
  const variants = useMemo(() => {
    const map = {};
    [...(movementsData || [])]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .forEach((row) => {
        const v = row?.variant;
        if (v?.id && !map[v.id]) map[v.id] = v;
      });
    return Object.values(map);
  }, [movementsData]);

  const filteredVariants = useMemo(() => {
    if (!filterInsumo) return variants;
    const q = filterInsumo.toLowerCase();
    return variants.filter((v) =>
      v.insumo?.nombre?.toLowerCase().includes(q) ||
      v.insumo?.codigo?.toLowerCase().includes(q) ||
      v.sku?.toLowerCase().includes(q) ||
      (v.attributes || []).some(
        (a) => a.attributeValue?.toLowerCase().includes(q) || a.attributeName?.toLowerCase().includes(q)
      )
    );
  }, [variants, filterInsumo]);

  const maxVariantStock = useMemo(
    () => Math.max(...variants.map((v) => parseFloat(v.stock || 0)), 1),
    [variants]
  );

  const loadMovements = useCallback(async () => {
    const all = await getAllInsumoComplete();
    const data = (all || []).filter((row) => {
      const matchInsumo = selectedInsumo
        ? row?.variant?.insumo?.id === selectedInsumo.id
        : true;
      const matchType = selectedType
        ? row?.movementType === selectedType.label
        : true;
      return matchInsumo && matchType;
    });
    setMovementsData(data);
  }, [selectedInsumo, selectedType]);

  useEffect(() => {
    const col = columns(setOpen, setMode, setOpenDelete, dispatch);
    setDataColumns(col);
    getAllInsumos().then((data) => setInsumos(data || []));
  }, [dispatch]);

  useEffect(() => {
    loadMovements();
  }, [loadMovements, open, openDelete]);

  const handleClose = () => setOpen(false);

  return (
    <Box sx={{ width: "100%" }}>
      <StackInsumosDelete openDelete={openDelete} setOpenDelete={setOpenDelete} />
      <StackInsumosDrawer open={open} handleClose={handleClose} mode={mode} variants={variants} />
      <ModalPDFInsumos open={openPDF} onClose={() => setOpenPDF(false)} variants={variants} />
      <VariantLabelModal open={openQR} handleClose={() => setOpenQR(false)} data={qrVariants} />

      <Grid container spacing={2}>
        {/* ─── IZQUIERDA: Movimientos ─── */}
        <Grid item xs={12} lg={6}>
          <Card extra="w-full h-full p-4">
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2 }}>
              <Box>
                <Typography
                  sx={{
                    fontFamily: "'Poppins', sans-serif", fontWeight: 700,
                    fontSize: "1rem", letterSpacing: "0.06em", textTransform: "uppercase",
                    background: "linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)",
                    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
                  }}
                >
                  Historial de movimientos
                </Typography>
                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.72rem", color: "#f472b6" }}>
                  Entradas y ajustes de inventario
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                <Autocomplete
                  options={insumos}
                  getOptionLabel={(o) => o.nombre || ""}
                  value={selectedInsumo}
                  onChange={(_, v) => { setSelectedInsumo(v); setSelectedType(null); }}
                  sx={{ width: 180 }}
                  size="small"
                  renderInput={(params) => <TextField {...params} label="Por insumo" size="small" />}
                />
                <Autocomplete
                  options={MOVEMENT_TYPES}
                  getOptionLabel={(o) => o.label}
                  value={selectedType}
                  onChange={(_, v) => { setSelectedType(v); setSelectedInsumo(null); }}
                  sx={{ width: 150 }}
                  size="small"
                  renderInput={(params) => <TextField {...params} label="Por tipo" size="small" />}
                  renderOption={(props, option) => (
                    <li {...props}><Chip label={option.label} color={option.color} size="small" /></li>
                  )}
                />
                <Tooltip title="Registrar nueva entrada" placement="top">
                  <button
                    onClick={() => { setOpen(true); setMode("POST"); }}
                    style={{
                      display: "flex", alignItems: "center", gap: 4,
                      background: "linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)",
                      border: "none", borderRadius: 8, padding: "7px 14px",
                      color: "#fff", cursor: "pointer",
                      fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: "0.8rem",
                      boxShadow: "0 3px 10px rgba(244,63,94,0.35)",
                    }}
                  >
                    <AddCircleIcon style={{ fontSize: 18 }} />
                    Nueva entrada
                  </button>
                </Tooltip>
              </Box>
            </Box>

            {/* Chips resumen */}
            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
              {MOVEMENT_TYPES.map((t) => {
                const count = (movementsData || []).filter((m) => m.movementType === t.label).length;
                return (
                  <Chip key={t.label} label={`${t.label}: ${count}`} color={t.color}
                    size="small" variant="outlined"
                    sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}
                  />
                );
              })}
            </Box>

            <Box sx={{ height: 550 }}>
              {movementsData ? (
                <DataGridMarifer rows={movementsData} columns={dataColumns || []} />
              ) : (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", pt: 10, gap: 2 }}>
                  <CircularProgress color="primary" size={24} />
                  <Typography sx={{ fontFamily: "'Poppins', sans-serif" }}>Cargando...</Typography>
                </Box>
              )}
            </Box>
          </Card>
        </Grid>

        {/* ─── DERECHA: Inventario actual ─── */}
        <Grid item xs={12} lg={6}>
          <Card extra="w-full h-full p-4">
            <TextField
              fullWidth size="small" placeholder="Buscar insumo, SKU o atributo..."
              value={filterInsumo}
              onChange={(e) => setFilterInsumo(e.target.value)}
              sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2, fontFamily: "'Poppins', sans-serif", fontSize: "0.82rem", "&.Mui-focused fieldset": { borderColor: "#f43f5e" } } }}
              InputProps={{ startAdornment: <span style={{ marginRight: 6, color: "#f472b6", fontSize: 16 }}>🔍</span> }}
            />
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Inventory2Icon sx={{ color: "#f43f5e", fontSize: 22 }} />
                <Box>
                  <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: "1rem", letterSpacing: "0.06em", textTransform: "uppercase", background: "linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                    Inventario actual
                  </Typography>
                  <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.72rem", color: "#f472b6" }}>
                    Stock por variante
                  </Typography>
                </Box>
              </Box>
              <Tooltip title="Generar reporte PDF" placement="top">
                <button
                  onClick={() => setOpenPDF(true)}
                  style={{ display: "flex", alignItems: "center", gap: 4, background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)", border: "none", borderRadius: 8, padding: "7px 14px", color: "#fff", cursor: "pointer", fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: "0.8rem", boxShadow: "0 3px 10px rgba(124,58,237,0.35)" }}
                >
                  <PictureAsPdfIcon style={{ fontSize: 18 }} />
                  Reporte PDF
                </button>
              </Tooltip>
            </Box>

          <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <Tooltip title="Imprimir etiquetas QR de las variantes seleccionadas" placement="top">
              <span>
                <button
                  disabled={selectedIds.length === 0}
                  onClick={() =>
                    printVariants(
                      filteredVariants.filter((v) => selectedIds.includes(v.id))
                    )
                  }
                  style={{
                    display: "flex", alignItems: "center", gap: 4,
                    background: selectedIds.length === 0 ? "#e5e7eb" : "linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)",
                    border: "none", borderRadius: 8, padding: "7px 14px",
                    color: selectedIds.length === 0 ? "#9ca3af" : "#fff",
                    cursor: selectedIds.length === 0 ? "not-allowed" : "pointer",
                    fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: "0.8rem",
                    boxShadow: selectedIds.length === 0 ? "none" : "0 3px 10px rgba(244,63,94,0.35)",
                  }}
                >
                  <QrCode2Icon style={{ fontSize: 18 }} />
                  Imprimir seleccionadas ({selectedIds.length})
                </button>
              </span>
            </Tooltip>
            <Tooltip title="Imprimir etiquetas QR de todas las variantes mostradas" placement="top">
              <span>
                <button
                  disabled={filteredVariants.length === 0}
                  onClick={() => printVariants(filteredVariants)}
                  style={{
                    display: "flex", alignItems: "center", gap: 4,
                    background: filteredVariants.length === 0 ? "#e5e7eb" : "linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)",
                    border: "none", borderRadius: 8, padding: "7px 14px",
                    color: filteredVariants.length === 0 ? "#9ca3af" : "#fff",
                    cursor: filteredVariants.length === 0 ? "not-allowed" : "pointer",
                    fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: "0.8rem",
                    boxShadow: filteredVariants.length === 0 ? "none" : "0 3px 10px rgba(14,165,233,0.35)",
                  }}
                >
                  <QrCode2Icon style={{ fontSize: 18 }} />
                  Imprimir todas
                </button>
              </span>
            </Tooltip>
          </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: 580, overflowY: "auto", pr: 1 }}>
              {filteredVariants.length === 0 && (
                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.82rem", color: "#9ca3af", textAlign: "center", pt: 4 }}>
                  Sin resultados
                </Typography>
              )}
              {filteredVariants.map((v) => {
                const stock = parseFloat(v.stock ?? 0);
                const pct = maxVariantStock > 0 ? (stock / maxVariantStock) * 100 : 0;
                const color = stock <= 0 ? "#ef4444" : stock < maxVariantStock * 0.2 ? "#f97316" : "#22c55e";
                return (
                  <Box
                    key={v.id}
                    sx={{ p: 1.5, borderRadius: 2, border: "1px solid #fce7f3", background: stock <= 0 ? "#fff1f2" : "#fff", transition: "box-shadow 0.2s", "&:hover": { boxShadow: "0 2px 12px rgba(244,63,94,0.15)" } }}
                  >
                    {/* Nombre + stock */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                        <Checkbox
                          size="small"
                          checked={selectedIds.includes(v.id)}
                          onChange={() => toggleSelected(v.id)}
                          sx={{ p: 0.25, color: "#f43f5e", "&.Mui-checked": { color: "#f43f5e" } }}
                        />
                        <Box>
                          <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: "0.82rem", color: "#374151" }}>
                            {v.insumo?.nombre}
                          </Typography>
                          <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.72rem", color: "#9ca3af" }}>
                            SKU: {v.sku || "-"} {v.insumo?.codigo ? `• ${v.insumo.codigo}` : ""}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Chip
                          label={`Stock: ${stock}`}
                          size="small"
                          sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: "0.72rem", background: color + "20", color, border: `1px solid ${color}40` }}
                        />
                        <Tooltip title="Imprimir etiqueta QR" placement="top">
                          <IconButton size="small" onClick={() => printVariants([v])} sx={{ color: "#f43f5e" }}>
                            <QrCode2Icon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Atributos */}
                    {(v.attributes || []).length > 0 && (
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 0.5 }}>
                        {(v.attributes || []).map((a) => (
                          <Chip
                            key={a.id}
                            label={`${a.attributeName}: ${a.attributeValue}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.65rem", borderColor: "#fce7f3", color: "#be185d" }}
                          />
                        ))}
                      </Box>
                    )}

                    {/* Barra de progreso */}
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(pct, 100)}
                      sx={{ height: 5, borderRadius: 3, backgroundColor: "#fce7f3", "& .MuiLinearProgress-bar": { backgroundColor: color, borderRadius: 3 } }}
                    />

                    {v.price != null && (
                      <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.65rem", color: "#9ca3af", mt: 0.5 }}>
                        Precio: ${parseFloat(v.price).toFixed(2)}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StackInsumosTable;

